/**
 * Drift correction.
 *
 * Even with perfect agreement about the time, two video decoders will not stay
 * level. Frame timing, dropped frames, audio clock pull and thermal throttling
 * all nudge a player off its intended position. Left alone, a few milliseconds
 * per minute becomes seconds per film.
 *
 * The naive fix is to seek back on target. It also ruins the film: seeking
 * flushes the decode pipeline, produces an audible gap and a visible stutter,
 * and it happens over and over.
 *
 * The right fix is to change *speed*, not position. Playing 0.5% fast is
 * inaudible and imperceptible, and it erases a 100 ms error in twenty seconds.
 * Seeking is kept in reserve for errors too large to walk off.
 *
 *          |error|          response
 *     -----------------------------------------------
 *          < 40 ms          nothing (deadband)
 *       40 .. 1500 ms       proportional rate nudge
 *          > 1500 ms        hard seek
 *
 * The deadband matters as much as the correction. Without it the controller
 * would chase noise forever, permanently holding a slightly wrong rate.
 */

export interface DriftPolicy {
  /** Errors smaller than this are ignored entirely. */
  readonly deadbandMs: number;
  /**
   * Once correcting, keep correcting until the error falls below this. Being
   * lower than the deadband is what gives the controller hysteresis, so it
   * settles cleanly instead of chattering on and off at the threshold.
   */
  readonly releaseMs: number;
  /** Errors at or above this are corrected by seeking instead. */
  readonly seekThresholdMs: number;
  /**
   * Time over which a correction aims to erase the error. Larger is gentler.
   * An error of 150 ms with a 3000 ms window asks for a 5% rate change.
   */
  readonly correctionWindowMs: number;
  /** Hard ceiling on the rate change, as a fraction. 0.05 is 5%. */
  readonly maxRateAdjust: number;
}

/**
 * Tuned for feature-length video on consumer hardware.
 *
 * 40 ms is a little over one frame at 24fps, which is below the threshold at
 * which lip sync errors are noticeable. 5% is the largest speed change that
 * remains unobtrusive on dialogue; beyond it, voices audibly shift pitch.
 */
export const DEFAULT_DRIFT_POLICY: DriftPolicy = {
  deadbandMs: 40,
  releaseMs: 15,
  seekThresholdMs: 1_500,
  correctionWindowMs: 3_000,
  maxRateAdjust: 0.05,
};

export type Correction =
  /** Already close enough. Play at the rate the user asked for. */
  | { readonly kind: 'hold'; readonly playbackRate: number }
  /** Nudge the speed to walk the error off without the viewer noticing. */
  | { readonly kind: 'rate'; readonly playbackRate: number; readonly adjust: number }
  /** Too far gone to walk back. Jump. */
  | { readonly kind: 'seek'; readonly toPositionMs: number };

/** Everything a caller needs to decide on a correction. */
export interface DriftInput {
  /** Where the room says we should be, in media milliseconds. */
  readonly targetPositionMs: number;
  /** Where this player actually is, in media milliseconds. */
  readonly actualPositionMs: number;
  /** The playback speed the user selected, before any correction. */
  readonly baseRate: number;
  /**
   * Suppresses correction while true. A buffering or seeking player has a
   * meaningless position, and correcting against it would inject a real error
   * in response to a phantom one.
   */
  readonly suspended: boolean;
}

/**
 * Stateful because correction needs hysteresis, and hysteresis needs to
 * remember whether it is mid-correction.
 *
 * Also accumulates the statistics shown in the sync readout: viewers trust a
 * number they can watch, and a visible accuracy figure turns "is this even
 * working" into something answerable.
 */
export class DriftController {
  readonly #policy: DriftPolicy;
  #correcting = false;
  #smoothedErrorMs = 0;
  #hasSmoothed = false;
  #seekCount = 0;
  #correctionCount = 0;
  #peakErrorMs = 0;

  constructor(policy: Partial<DriftPolicy> = {}) {
    this.#policy = { ...DEFAULT_DRIFT_POLICY, ...policy };
  }

  /**
   * Decide what to do about the current error.
   *
   * Positive error means the player is behind the room and must speed up.
   */
  evaluate(input: DriftInput): Correction {
    const { targetPositionMs, actualPositionMs, baseRate, suspended } = input;

    if (suspended) {
      this.#correcting = false;
      return { kind: 'hold', playbackRate: baseRate };
    }

    const errorMs = targetPositionMs - actualPositionMs;
    this.#record(errorMs);

    const magnitude = Math.abs(errorMs);

    if (magnitude >= this.#policy.seekThresholdMs) {
      this.#correcting = false;
      this.#seekCount += 1;
      return { kind: 'seek', toPositionMs: targetPositionMs };
    }

    // Hysteresis: entering correction takes a larger error than staying in it.
    const threshold = this.#correcting ? this.#policy.releaseMs : this.#policy.deadbandMs;
    if (magnitude < threshold) {
      this.#correcting = false;
      return { kind: 'hold', playbackRate: baseRate };
    }

    if (!this.#correcting) this.#correctionCount += 1;
    this.#correcting = true;

    const adjust = clamp(
      errorMs / this.#policy.correctionWindowMs,
      -this.#policy.maxRateAdjust,
      this.#policy.maxRateAdjust,
    );

    return {
      kind: 'rate',
      // Guarded against zero so a pathological base rate can never stall the
      // player in a way only a reload would clear.
      playbackRate: Math.max(0.0625, baseRate * (1 + adjust)),
      adjust,
    };
  }

  /** Live statistics for the diagnostics panel and the sync accuracy readout. */
  get stats(): DriftStats {
    return {
      smoothedErrorMs: this.#hasSmoothed ? this.#smoothedErrorMs : 0,
      peakErrorMs: this.#peakErrorMs,
      seekCount: this.#seekCount,
      correctionCount: this.#correctionCount,
      correcting: this.#correcting,
    };
  }

  /** Clear counters and hysteresis, for a new media item or a fresh room. */
  reset(): void {
    this.#correcting = false;
    this.#smoothedErrorMs = 0;
    this.#hasSmoothed = false;
    this.#seekCount = 0;
    this.#correctionCount = 0;
    this.#peakErrorMs = 0;
  }

  #record(errorMs: number): void {
    const magnitude = Math.abs(errorMs);
    if (magnitude > this.#peakErrorMs) this.#peakErrorMs = magnitude;

    // Exponentially weighted average. Displaying the raw per-tick error would
    // produce a number that flickers too fast to read.
    const alpha = 0.2;
    this.#smoothedErrorMs = this.#hasSmoothed
      ? this.#smoothedErrorMs * (1 - alpha) + magnitude * alpha
      : magnitude;
    this.#hasSmoothed = true;
  }
}

export interface DriftStats {
  /** Smoothed absolute error, the figure shown as "sync accuracy". */
  readonly smoothedErrorMs: number;
  /** Worst error seen since the last reset. */
  readonly peakErrorMs: number;
  /** How many times correction had to fall back to a seek. */
  readonly seekCount: number;
  /** How many distinct correction episodes have occurred. */
  readonly correctionCount: number;
  /** Whether a correction is in progress right now. */
  readonly correcting: boolean;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
